import { useEffect, useState } from 'react';
import {
  Alert, Button, Divider, Drawer, Form, Input, Popconfirm,
  Select, Space, Switch, Table, Tag, Tooltip, Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  createModuleActionRule,
  deleteModuleActionRule,
  getModules,
  getModuleActionRules,
  getModuleFields,
  getRowActionConfigs,
  updateModuleActionRule,
} from '../../../services/adminApi';

const { Text, Title } = Typography;

const OPERATOR_OPTIONS = [
  { value: 'eq',       label: 'equals' },
  { value: 'ne',       label: 'not equals' },
  { value: 'isEmpty',  label: 'is empty' },
  { value: 'notEmpty', label: 'is not empty' },
  { value: 'contains', label: 'contains' },
  { value: 'fieldEq',  label: 'field = field' },
  { value: 'scope',    label: 'view scope' },
];

const SCOPE_VALUES = [
  { value: 'myJobs',  label: 'My Jobs' },
  { value: 'allJobs', label: 'All Jobs' },
];

const VALUE_NEEDED     = ['eq', 'ne', 'contains'];
const SCOPE_OP         = 'scope';
const FIELD_EQ_OP      = 'fieldEq';

// Default rules for the job module — mirrors the previously hardcoded backend logic
const JOB_SEED_RULES = [
  {
    name: 'Fulfilled jobs — block sourcing',
    description: 'When a job status is fulfilled and has no recruiters, hide source & quick-submit buttons.',
    conditions: [
      { field: 'jobRecruitmentStatus', operator: 'eq', value: 'fulfilled', compareField: '' },
      { field: 'recruiters',           operator: 'isEmpty', value: '', compareField: '' },
    ],
    blockedActions: ['sourceCandidates', 'quickSubmit'],
    isActive: true,
  },
  {
    name: 'All Jobs scope — restrict actions',
    description: 'When viewing All Jobs, hide sourcing, quick-submit, change-status, notes and share.',
    conditions: [
      { field: '', operator: 'scope', value: 'allJobs', compareField: '' },
    ],
    blockedActions: ['sourceCandidates', 'quickSubmit', 'changeStatus', 'addNotes', 'share'],
    isActive: true,
  },
  {
    name: 'Target submission reached — block sourcing',
    description: 'When targetSubmission equals submissionCount, no more sourcing is needed.',
    conditions: [
      { field: 'targetSubmission', operator: 'fieldEq', value: '', compareField: 'submissionCount' },
    ],
    blockedActions: ['sourceCandidates', 'quickSubmit'],
    isActive: true,
  },
];

const emptyCondition = () => ({ field: '', operator: 'eq', value: '', compareField: '' });
const emptyRule      = () => ({ name: '', description: '', conditions: [emptyCondition()], blockedActions: [], isActive: true });

function operatorLabel(op) {
  return OPERATOR_OPTIONS.find((o) => o.value === op)?.label ?? op;
}

function conditionSummary(c) {
  if (c.operator === SCOPE_OP)   return `scope = ${c.value}`;
  if (c.operator === FIELD_EQ_OP) return `${c.field} = field:${c.compareField}`;
  if (['isEmpty','notEmpty'].includes(c.operator)) return `${c.field} ${operatorLabel(c.operator)}`;
  return `${c.field} ${operatorLabel(c.operator)} "${c.value}"`;
}

function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function normalizeModuleOption(module) {
  if (typeof module === 'string') {
    return { value: module, label: moduleTitle(module) };
  }
  const value = (
    module?.module ?? module?.key ?? module?.moduleKey ?? module?.value ??
    module?.apiModule ?? module?.name ?? module?.modulename ?? module?.menuName
  );
  if (!value) return null;
  return {
    value,
    label: (
      module?.label ?? module?.moduleLabel ?? module?.moduleName ??
      module?.name ?? module?.modulename ?? module?.menuName ?? moduleTitle(value)
    ),
  };
}

function normalizeModuleOptions(modules) {
  const seen = new Set();
  return (Array.isArray(modules) ? modules : [])
    .map(normalizeModuleOption)
    .filter(Boolean)
    .filter((module) => {
      if (seen.has(module.value)) return false;
      seen.add(module.value);
      return true;
    });
}

export default function ModuleActionRulesSection() {
  const [moduleOptions, setModuleOptions]     = useState([]);
  const [modulesLoading, setModulesLoading]   = useState(false);
  const [selectedModule, setSelectedModule]   = useState('');
  const [rules, setRules]                     = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [drawerOpen, setDrawerOpen]           = useState(false);
  const [editing, setEditing]                 = useState(null);
  const [form, setForm]                       = useState(emptyRule());
  const [saving, setSaving]                   = useState(false);
  const [seeding, setSeeding]                 = useState(false);
  const [fieldOptions, setFieldOptions]       = useState([]);   // module fields for condition picker
  const [actionKeyOptions, setActionKeyOptions] = useState([]); // row action keys for blocked actions

  useEffect(() => {
    let cancelled = false;
    setModulesLoading(true);
    getModules()
      .then((modules) => {
        if (cancelled) return;
        const options = normalizeModuleOptions(modules);
        setModuleOptions(options);
        setSelectedModule((current) => (
          options.some((option) => option.value === current) ? current : options[0]?.value ?? ''
        ));
      })
      .catch(() => {
        if (!cancelled) {
          setModuleOptions([]);
          setSelectedModule('');
        }
      })
      .finally(() => {
        if (!cancelled) setModulesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── Load rules ──────────────────────────────────────────────────────────────
  function load(mod) {
    setLoading(true);
    getModuleActionRules(mod)
      .then((data) => setRules(Array.isArray(data) ? data : []))
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  }

  // ── Load field options for condition field picker ────────────────────────────
  function loadFieldOptions(mod) {
    getModuleFields(mod)
      .then((fields) => {
        const opts = (Array.isArray(fields) ? fields : []).map((f) => ({
          value: f.field ?? f.fieldKey ?? f.key ?? f.name,
          label: `${f.label ?? f.fieldName ?? f.name} (${f.field ?? f.fieldKey ?? f.key ?? f.name})`,
        })).filter((o) => o.value);
        setFieldOptions(opts);
      })
      .catch(() => setFieldOptions([]));
  }

  // ── Load row action keys for blocked actions picker ──────────────────────────
  function loadActionKeyOptions(mod) {
    getRowActionConfigs(mod)
      .then((configs) => {
        const keys = new Set();
        (Array.isArray(configs) ? configs : []).forEach((cfg) => {
          (cfg.rowActions ?? []).forEach((a) => { if (a.key) keys.add(a.key); });
        });
        setActionKeyOptions([...keys].map((k) => ({ value: k, label: k })));
      })
      .catch(() => setActionKeyOptions([]));
  }

  useEffect(() => {
    if (!selectedModule) {
      setRules([]);
      setFieldOptions([]);
      setActionKeyOptions([]);
      return;
    }
    load(selectedModule);
    loadFieldOptions(selectedModule);
    loadActionKeyOptions(selectedModule);
  }, [selectedModule]);

  // ── Drawer helpers ───────────────────────────────────────────────────────────
  function openCreate() {
    setEditing(null);
    setForm(emptyRule());
    setDrawerOpen(true);
  }

  function openEdit(rule) {
    setEditing(rule);
    setForm({
      name:           rule.name           ?? '',
      description:    rule.description    ?? '',
      conditions:     rule.conditions?.length
                        ? rule.conditions.map((c) => ({ ...c }))
                        : [emptyCondition()],
      blockedActions: rule.blockedActions ?? [],
      isActive:       rule.isActive       ?? true,
    });
    setDrawerOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await updateModuleActionRule(editing.id, form);
      } else {
        await createModuleActionRule({ ...form, module: selectedModule });
      }
      setDrawerOpen(false);
      load(selectedModule);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(rule) {
    await deleteModuleActionRule(rule.id);
    load(selectedModule);
  }

  // ── Seed default rules for job module ────────────────────────────────────────
  async function handleSeedDefaults() {
    setSeeding(true);
    try {
      for (const rule of JOB_SEED_RULES) {
        await createModuleActionRule({ ...rule, module: selectedModule });
      }
      load(selectedModule);
    } finally {
      setSeeding(false);
    }
  }

  // ── Condition builder helpers ────────────────────────────────────────────────
  function updateCondition(idx, patch) {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  }

  function addCondition() {
    setForm((prev) => ({ ...prev, conditions: [...prev.conditions, emptyCondition()] }));
  }

  function removeCondition(idx) {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== idx),
    }));
  }

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Rule Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (v, r) => (
        <div>
          <Text strong>{v}</Text>
          {r.description ? <div><Text type="secondary" style={{ fontSize: 11 }}>{r.description}</Text></div> : null}
        </div>
      ),
    },
    {
      title: 'Conditions (ALL must match)',
      key: 'conditions',
      render: (_, r) => (
        <Space size={4} direction="vertical">
          {(r.conditions ?? []).map((c, i) => (
            <Tag key={i} style={{ fontFamily: 'monospace', fontSize: 11, margin: 0 }}>
              {conditionSummary(c)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Blocked Row Actions',
      key: 'blockedActions',
      render: (_, r) => (
        <Space size={4} wrap>
          {(r.blockedActions ?? []).map((k) => <Tag key={k} color="volcano">{k}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'On' : 'Off'}</Tag>,
    },
    {
      title: '',
      key: 'act',
      width: 80,
      render: (_, r) => (
        <Space>
          <Tooltip title="Edit"><Button icon={<EditOutlined />} size="small" type="text" onClick={() => openEdit(r)} /></Tooltip>
          <Popconfirm title="Delete this rule?" onConfirm={() => handleDelete(r)}>
            <Tooltip title="Delete"><Button danger icon={<DeleteOutlined />} size="small" type="text" /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px 0' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <Title level={5} style={{ margin: 0 }}>Action Rules</Title>
        <Select
          options={moduleOptions}
          value={selectedModule}
          loading={modulesLoading}
          placeholder="Select module"
          onChange={setSelectedModule}
          style={{ width: 200 }}
        />
        <Button icon={<PlusOutlined />} type="primary" disabled={!selectedModule} onClick={openCreate}>
          Add Rule
        </Button>
        {(selectedModule === 'job' || selectedModule === 'jobs') && (
          <Tooltip title="Create the 3 standard job rules that replace the hardcoded backend logic">
            <Button
              icon={<ThunderboltOutlined />}
              loading={seeding}
              onClick={handleSeedDefaults}
            >
              Seed Job Defaults
            </Button>
          </Tooltip>
        )}
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, fontSize: 12 }}
        message={
          <span>
            Rules are evaluated <b>per row</b> in the list view using the record&apos;s own field values.
            When <b>all conditions</b> of a rule match, the listed action buttons are hidden for that row.
            The <b>key</b> of each blocked action must match what is set in <b>Row Actions</b> config.
          </span>
        }
      />

      <Table
        columns={columns}
        dataSource={rules}
        loading={loading}
        rowKey="id"
        size="small"
        pagination={false}
        locale={{ emptyText: selectedModule ? 'No rules yet. Click "Add Rule" to get started.' : 'No modules found. Configure modules first.' }}
      />

      {/* ── Add / Edit Drawer ─────────────────────────────────────────────── */}
      <Drawer
        open={drawerOpen}
        title={editing ? `Edit: ${editing.name}` : 'Add Action Rule'}
        width={620}
        onClose={() => setDrawerOpen(false)}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>Save Rule</Button>
          </Space>
        }
      >
        <Form layout="vertical">

          <Form.Item label="Rule Name" required>
            <Input
              value={form.name}
              placeholder="e.g. Fulfilled jobs — block sourcing"
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </Form.Item>

          <Form.Item label="Description">
            <Input.TextArea
              rows={2}
              value={form.description}
              placeholder="Explain when this rule applies"
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </Form.Item>

          <Form.Item label="Active">
            <Switch checked={form.isActive} onChange={(v) => setForm((p) => ({ ...p, isActive: v }))} />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0} style={{ fontSize: 13 }}>
            Conditions <Text type="secondary" style={{ fontSize: 12 }}>(ALL must match for the rule to fire)</Text>
          </Divider>

          {form.conditions.map((cond, idx) => (
            <div
              key={idx}
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                padding: '10px 12px',
                marginBottom: 8,
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>

                {/* Operator selector — first so user picks condition type */}
                <Form.Item label="Operator" style={{ marginBottom: 0, minWidth: 140 }}>
                  <Select
                    options={OPERATOR_OPTIONS}
                    value={cond.operator}
                    style={{ width: 140 }}
                    onChange={(v) => updateCondition(idx, { operator: v, value: '', compareField: '' })}
                  />
                </Form.Item>

                {/* Field picker — hidden for scope operator */}
                {cond.operator !== SCOPE_OP && (
                  <Form.Item label="Record Field" style={{ marginBottom: 0, flex: 1, minWidth: 180 }}>
                    <Select
                      showSearch
                      allowClear
                      mode="tags"
                      maxCount={1}
                      value={cond.field ? [cond.field] : []}
                      options={fieldOptions}
                      style={{ width: '100%' }}
                      placeholder="Select or type field name"
                      onChange={(v) => updateCondition(idx, { field: v[v.length - 1] ?? '' })}
                      filterOption={(input, opt) =>
                        (opt?.label ?? opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Field from the record (e.g. <code>jobRecruitmentStatus</code>, <code>recruiters</code>)
                    </Text>
                  </Form.Item>
                )}

                {/* Value input — only for operators that compare to a constant */}
                {VALUE_NEEDED.includes(cond.operator) && (
                  <Form.Item label="Value" style={{ marginBottom: 0, minWidth: 140 }}>
                    <Input
                      value={cond.value}
                      placeholder='e.g. "fulfilled"'
                      style={{ width: 150 }}
                      onChange={(e) => updateCondition(idx, { value: e.target.value })}
                    />
                  </Form.Item>
                )}

                {/* Scope value — only for scope operator */}
                {cond.operator === SCOPE_OP && (
                  <Form.Item label="Scope Value" style={{ marginBottom: 0, minWidth: 140 }}>
                    <Select
                      options={SCOPE_VALUES}
                      value={cond.value || undefined}
                      style={{ width: 150 }}
                      placeholder="Select scope"
                      onChange={(v) => updateCondition(idx, { value: v })}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Current view tab (myJobs / allJobs)
                    </Text>
                  </Form.Item>
                )}

                {/* Compare field — only for fieldEq operator */}
                {cond.operator === FIELD_EQ_OP && (
                  <Form.Item label="Compare To Field" style={{ marginBottom: 0, minWidth: 160 }}>
                    <Select
                      showSearch
                      allowClear
                      mode="tags"
                      maxCount={1}
                      value={cond.compareField ? [cond.compareField] : []}
                      options={fieldOptions}
                      style={{ width: 170 }}
                      placeholder="Select or type field"
                      onChange={(v) => updateCondition(idx, { compareField: v[v.length - 1] ?? '' })}
                      filterOption={(input, opt) =>
                        (opt?.label ?? opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      e.g. <code>submissionCount</code>
                    </Text>
                  </Form.Item>
                )}

                {/* Remove condition */}
                <div style={{ paddingTop: 22 }}>
                  <Button
                    danger
                    disabled={form.conditions.length <= 1}
                    icon={<DeleteOutlined />}
                    size="small"
                    type="text"
                    onClick={() => removeCondition(idx)}
                  />
                </div>
              </div>

              {/* Preview of what the condition means */}
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Preview: <code style={{ color: '#1677ff' }}>{conditionSummary(cond)}</code>
                </Text>
              </div>
            </div>
          ))}

          <Button size="small" style={{ marginBottom: 16 }} onClick={addCondition}>
            + Add Condition
          </Button>

          <Divider orientation="left" orientationMargin={0} style={{ fontSize: 13 }}>
            Blocked Row Actions
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              (hidden when this rule fires)
            </Text>
          </Divider>

          <Form.Item>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              value={form.blockedActions}
              options={actionKeyOptions}
              placeholder="Select or type action key (e.g. sourceCandidates, quickSubmit)"
              onChange={(v) => setForm((p) => ({ ...p, blockedActions: v }))}
              filterOption={(input, opt) =>
                (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Must match the <b>key</b> of a Row Action (configured in Admin → Row Actions).
              Common job keys: <code>sourceCandidates</code>, <code>quickSubmit</code>,{' '}
              <code>changeStatus</code>, <code>share</code>, <code>addNotes</code>, <code>assign</code>.
            </Text>
          </Form.Item>

        </Form>
      </Drawer>
    </div>
  );
}
